import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetFTLConsolidatedDataQuery } from '@/redux/slices/pallet/palletApiSlice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSummaryNumber } from '@/lib/formatSummaryNumber';

interface ConsolidatedViewProps {
  isOpen?: boolean;
  onClose?: () => void;
  variant: string;
}

const SummaryFtl: React.FC<ConsolidatedViewProps> = ({ variant }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('average');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetFTLConsolidatedDataQuery({ projectId, variant }, { refetchOnMountOrArgChange: true });
  console.log(consolidatedData,'consolidatedData');
  if (isLoading) return (
    <div className="space-y-6 w-full animate-pulse">
      {/* First Statistics Table Skeleton - Complex table with multiple columns */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-14 p-3">
            {Array(14).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-1" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="grid grid-cols-14 gap-1 p-2 border-b last:border-0">
              {Array(14).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Second Statistics Table Skeleton */}
      <div className="border rounded-md overflow-hidden mt-6">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-10 p-3">
            {Array(10).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-1" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="grid grid-cols-10 gap-1 p-2 border-b last:border-0">
              {Array(10).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Third Statistics Table Skeleton */}
      <div className="border rounded-md overflow-hidden mt-6">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-13 p-3">
            {Array(13).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-1" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="grid grid-cols-13 gap-1 p-2 border-b last:border-0">
              {Array(13).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  if (error) return <div>Error loading data</div>;

  const renderRawDataTable = () => (
    <Table className="border-collapse w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Pallet Name</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Pallet Type</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Measured</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Measured</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Measured</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {consolidatedData.rawData.map((row: any, index: number) => (
          <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
            <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.topMeasured}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.middleMeasured}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.bottomMeasured}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  const renderStatisticsTable = () => (
    <div className="space-y-6">
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Weight</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Calculated FTL Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Plus By user</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Percentage</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Top</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Middle</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Bottom</TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Lower Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Upper Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Lower Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Upper Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Lower Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Upper Range</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.palletDetails.map((pallet: any, index: number) => (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.weight}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.calculatedFTLTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.plusByUser}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.percentage}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.topCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.topMin).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{formatSummaryNumber(parseFloat(pallet.topCalculatedTarget) + parseFloat(pallet.plusByUser))}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.middleCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.middleMin).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{formatSummaryNumber(Number(pallet.middleCalculatedTarget) + Number(pallet.plusByUser))}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.bottomCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.bottomMin).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{formatSummaryNumber(Number(pallet.bottomCalculatedTarget) + Number(pallet.plusByUser))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Top</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Middle</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Bottom</TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.statistics.min.map((row: any, index: number) => (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.bottom)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].bottom)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].bottom)).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Pallet Name</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Top Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Middle Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Bottom Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Data Fill Status</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">FTL Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Below Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Below Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Below Recommended Count</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfPalletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfTopMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfMiddleMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfBottomMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
              <TableCell
                className={cn(
                  "border border-gray-300 text-center p-2",
                  {
                    "text-white bg-bgRed": row.ftlRange === 'Fail',
                    "text-white bg-states-success": row.ftlRange === 'Pass' && row.dataFillStatus !== 'Incomplete',
                    "text-white bg-[#F79E19]": row.ftlRange === 'Pass' && row.dataFillStatus === 'Incomplete',
                  }
                )}
              >
                {row.ftlRange === 'Pass' && row.dataFillStatus === 'Incomplete' ? (
                  "Data Missing"
                ) : (
                  row.ftlRange
                )}
              </TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.topAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.topAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.topBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.topBelowRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.middleAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.middleAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.middleBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.middleBelowRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.bottomAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.bottomAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center text-white p-2", parseInt(row.bottomBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.bottomBelowRecommendedCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletName,
      Top: Math.abs(parseFloat(item.top)),
      Middle: Math.abs(parseFloat(item.middle)),
      Bottom: Math.abs(parseFloat(item.bottom)),
    }));

    const colors = {
      Top: "#004899",
      Middle: "#82ca9d",
      Bottom: "#ffc658"
    };

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['average', 'max', 'min', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start hover:bg-blue hover:text-white",
                activeStatistic === stat ? "bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="Top" fill={colors.Top} />
            <Bar barSize={20} dataKey="Middle" fill={colors.Middle} />
            <Bar barSize={20} dataKey="Bottom" fill={colors.Bottom} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };
  return (

    <div className="flex-1 p-6 overflow-auto">

      {renderStatisticsTable()}

    </div>

  );
};

export default SummaryFtl;
